import type { ParseKeys } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from '@/components/Icons/Info';
import { useProfileStatus } from '@/hooks/useProfileStatus';
import { ProfileStatus } from '@/types/user';
import { tv } from '@/utils/tv';

const banner = tv({ base: 'px-4 pt-3 pb-4 md:px-6 md:pt-6 md:pb-0' });

const statusKeys: Record<
  ProfileStatus,
  { title: ParseKeys; description: ParseKeys }
> = {
  [ProfileStatus.NextStudyYear]: {
    title: 'profileStatus.nextStudyYear.title',
    description: 'profileStatus.nextStudyYear.description',
  },
  [ProfileStatus.PastStudyYear]: {
    title: 'profileStatus.pastStudyYear.title',
    description: 'profileStatus.pastStudyYear.description',
  },
  [ProfileStatus.InactiveProfile]: {
    title: 'profileStatus.inactiveProfile.title',
    description: 'profileStatus.inactiveProfile.description',
  },
};

interface Props {
  className?: string;
}

export const ProfileStatusBanner: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const status = useProfileStatus();

  if (!status) {
    return null;
  }

  const { title, description } = statusKeys[status];

  return (
    <div className={banner({ className })}>
      <div className="flex flex-col gap-2 rounded-xl border border-primary bg-card p-4 shadow-card contrast:border-border md:shadow-none">
        <div className="inline-flex w-fit items-center gap-1 rounded-xl border border-transparent bg-purple-soft py-[2px] pr-2 pl-1 text-caption-12 text-primary contrast:border-border">
          <Info className="size-4 shrink-0" />
          {t(title)}
        </div>
        <p className="text-body-13 text-muted-foreground md:text-body-16">
          {t(description)}
        </p>
      </div>
    </div>
  );
};
